import React, { useState, useRef, useCallback, useEffect } from 'react';
import { Upload, Globe, X, ArrowRight, Download, Trash2, Play, Pause, Loader2 } from 'lucide-react';
import { useParams, Link } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { toast } from 'react-toastify';
import { C } from '../styles/tokens';
import { API } from '../utils/amplifyWrapper';
import { downloadFile } from '../utils/downloadFile';
import { fetchDubbingPresignedUrl, uploadToS3, useDubbingJobs, useCreateDubbingJob, useDeleteDubbingJob } from '../http-hooks/dubbing/useDubbing';
import GenerationsTable from '../components/media/GenerationsTable';
import ConfirmDialog from '../components/media/ConfirmDialog';
import FeatureCreditCost from '../components/plans/FeatureCreditCost';
import { useFeatureCreditGate } from '../http-hooks/organisation/useFeatureCreditEstimate';
import { SearchableSelect } from '../components/ui/SearchableSelect';
import useIsMobile from '../hooks/useIsMobile';
import { addMixPanelEvent } from '../store/actions';
import { useGlobalStore } from '../zustand-store/useGlobalStore';

// Best-effort MIME when the browser reports an empty file.type (common for .flac/.m4a).
const EXT_MIME = { mp3: 'audio/mpeg', wav: 'audio/wav', m4a: 'audio/mp4', aac: 'audio/aac', flac: 'audio/flac', mp4: 'video/mp4', mov: 'video/quicktime', avi: 'video/x-msvideo' };
const contentTypeFor = file => file.type || EXT_MIME[(file.name.split('.').pop() || '').toLowerCase()] || 'audio/mpeg';

const YOUTUBE_REGEX = /^(https?:\/\/)?(www\.)?(youtube\.com\/(watch\?v=|shorts\/|embed\/)|youtu\.be\/)[\w-]{11}/;


const DUBBING_LANGUAGES = [
    { code: 'ar', name: 'Arabic' },
    { code: 'da', name: 'Danish' },
    { code: 'nl', name: 'Dutch' },
    { code: 'en', name: 'English' },
    { code: 'fi', name: 'Finnish' },
    { code: 'fr', name: 'French' },
    { code: 'de', name: 'German' },
    { code: 'el', name: 'Greek' },
    { code: 'he', name: 'Hebrew' },
    { code: 'hi', name: 'Hindi' },
    { code: 'id', name: 'Indonesian' },
    { code: 'it', name: 'Italian' },
    { code: 'ms', name: 'Malay' },
    { code: 'no', name: 'Norwegian' },
    { code: 'pl', name: 'Polish' },
    { code: 'pt', name: 'Portuguese' },
    { code: 'ru', name: 'Russian' },
    { code: 'es', name: 'Spanish' },
    { code: 'sv', name: 'Swedish' },
    { code: 'tl', name: 'Tagalog' },
    { code: 'th', name: 'Thai' },
    { code: 'tr', name: 'Turkish' },
    { code: 'vi', name: 'Vietnamese' },
];

const ACCEPTED_TYPES = '.mp3,.wav,.m4a,.aac,.flac,.mp4,.mov,.avi,audio/*,video/*';
const MAX_UPLOAD_SIZE_BYTES = 50 * 1024 * 1024;
const MAX_UPLOAD_DURATION_SEC = 600;


function readMediaDuration(file) {
    return new Promise((resolve, reject) => {
        const url = URL.createObjectURL(file);
        const el = document.createElement('video');
        el.preload = 'metadata';
        el.onloadedmetadata = () => {
            URL.revokeObjectURL(url);
            if (!Number.isFinite(el.duration)) {
                reject(new Error('Could not determine file duration.'));
                return;
            }
            resolve(el.duration);
        };
        el.onerror = () => {
            URL.revokeObjectURL(url);
            reject(new Error('Could not read file metadata.'));
        };
        el.src = url;
    });
}

const VIDEO_MIME_PREFIXES = ['video/'];
const isVideoFile = file => file && VIDEO_MIME_PREFIXES.some(p => file.type.startsWith(p));

// ── Helpers ───────────────────────────────────────────────────────────────────

function formatDuration(seconds) {
    const h = Math.floor(seconds / 3600);
    const m = Math.floor((seconds % 3600) / 60);
    const s = seconds % 60;
    if (h > 0) return `${h}:${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
    return `${m}:${String(s).padStart(2, '0')}`;
}

// ── YouTube preview card ───────────────────────────────────────────────────────

function YouTubePreview({ metadata, onClear }) {
    return (
        <div style={{
            display: 'flex', alignItems: 'center', gap: 12,
            padding: 12, border: `1px solid ${C.n200}`, borderRadius: 12, background: C.n50,
        }}>
            <img
                src={metadata.thumbnail}
                alt={metadata.title}
                style={{ width: 112, height: 63, objectFit: 'cover', borderRadius: 6, flexShrink: 0, background: C.n200 }}
            />
            <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{
                    fontSize: 13.5, fontWeight: 600, color: C.n800,
                    overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                }}>
                    {metadata.title}
                </div>
                <div style={{ fontSize: 12, color: C.n400, marginTop: 4 }}>
                    {formatDuration(metadata.durationSeconds)}
                </div>
            </div>
            <button
                type="button"
                onClick={onClear}
                title="Use a different URL"
                style={{
                    flexShrink: 0, background: 'none', border: `1px solid ${C.n200}`,
                    borderRadius: 6, padding: '4px 8px', cursor: 'pointer',
                    color: C.n500, fontSize: 12, fontFamily: 'inherit',
                    display: 'flex', alignItems: 'center', gap: 4,
                }}
            >
                <X size={12} /> Change
            </button>
        </div>
    );
}

// ── Upload zone ───────────────────────────────────────────────────────────────

function UploadZone({ onFileSelect, selectedFile, uploading, error, onClear }) {
    const [isDragOver, setIsDragOver] = useState(false);
    const inputRef = useRef(null);

    const handleDrop = useCallback((e) => {
        e.preventDefault();
        setIsDragOver(false);
        const file = e.dataTransfer.files[0];
        if (file) onFileSelect(file);
    }, [onFileSelect]);

    return (
        <div
            onClick={() => !selectedFile && inputRef.current?.click()}
            onDragOver={e => { e.preventDefault(); setIsDragOver(true); }}
            onDragLeave={() => setIsDragOver(false)}
            onDrop={handleDrop}
            style={{
                display: 'flex', alignItems: 'center', gap: 14,
                padding: '28px 24px',
                border: `1.5px dashed ${isDragOver ? C.p600 : C.n300}`,
                borderRadius: 12,
                background: isDragOver ? C.p50 : 'transparent',
                cursor: selectedFile ? 'default' : 'pointer',
                transition: 'all .15s',
            }}
        >
            <div style={{
                width: 44, height: 44, borderRadius: 10,
                background: C.n100,
                display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
            }}>
                <Upload size={22} color={C.n500} strokeWidth={1.75} />
            </div>
            <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 14, fontWeight: 600, color: C.n800, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {selectedFile ? selectedFile.name : 'Click or drag to upload here'}
                </div>
                <div style={{ fontSize: 12.5, color: error ? '#DC2626' : C.n400, marginTop: 2 }}>
                    {uploading
                        ? 'Uploading…'
                        : error
                            ? error
                            : selectedFile
                                ? `${(selectedFile.size / 1024 / 1024).toFixed(1)} MB`
                                : 'Audio or video file, up to 50MB or 10 minutes'
                    }
                </div>
            </div>
            {selectedFile && (
                <button
                    type="button"
                    onClick={e => { e.stopPropagation(); onClear(); }}
                    style={{
                        flexShrink: 0, display: 'flex', alignItems: 'center', gap: 4,
                        background: C.n100, border: 'none', borderRadius: 7,
                        padding: '5px 12px', cursor: 'pointer', color: C.n600,
                        fontSize: 12.5, fontWeight: 500, fontFamily: 'inherit',
                    }}
                >
                    <X size={12} /> Remove
                </button>
            )}
            <input
                ref={inputRef}
                type="file"
                accept={ACCEPTED_TYPES}
                style={{ display: 'none' }}
                onChange={e => {
                    const file = e.target.files[0];
                    e.target.value = '';
                    if (file) onFileSelect(file);
                }}
            />
        </div>
    );
}

// ── URL input ─────────────────────────────────────────────────────────────────

function URLInput({ value, onChange, placeholder }) {
    return (
        <input
            type="url"
            placeholder={placeholder}
            value={value}
            onChange={e => onChange(e.target.value)}
            style={{
                width: '100%', boxSizing: 'border-box',
                padding: '10px 14px', border: `1px solid ${C.n200}`, borderRadius: 8,
                fontSize: 13.5, color: C.n900, background: C.w,
                outline: 'none', fontFamily: 'inherit',
            }}
            onFocus={e => { e.target.style.borderColor = C.p600; e.target.style.boxShadow = `0 0 0 3px ${C.p100}`; }}
            onBlur={e => { e.target.style.borderColor = C.n200; e.target.style.boxShadow = 'none'; }}
        />
    );
}

// ── Audio / Video toggle (used for Other URL tab) ─────────────────────────────

function MediaTypeToggle({ value, onChange }) {
    return (
        <div style={{ display: 'inline-flex', background: C.n100, borderRadius: 7, padding: 2, gap: 1, marginTop: 8 }}>
            {['audio', 'video'].map(type => (
                <button
                    key={type}
                    type="button"
                    onClick={() => onChange(type)}
                    style={{
                        padding: '4px 14px', borderRadius: 5, border: 'none',
                        background: value === type ? C.w : 'transparent',
                        color: value === type ? C.n800 : C.n500,
                        fontSize: 12.5, fontWeight: value === type ? 600 : 400,
                        cursor: 'pointer', fontFamily: 'inherit',
                        boxShadow: value === type ? '0 1px 3px rgba(0,0,0,.08)' : 'none',
                        transition: 'all .12s', textTransform: 'capitalize',
                    }}
                >
                    {type}
                </button>
            ))}
        </div>
    );
}

// ── Generations (dub history) ──────────────────────────────────────────────────

// Extension for a download filename, derived from the output URL (strips any query string).
const extOf = url => (url.split('?')[0].split('.').pop() || 'mp3').toLowerCase();

function DubRow({ job, org_id, integration_id, gridTemplateColumns, playingId, setPlayingId, isMobile }) {
    const deleteDub = useDeleteDubbingJob();
    const [isPlaying, setIsPlaying] = useState(false);
    const [loadingAudio, setLoadingAudio] = useState(false);
    const [downloadingLang, setDownloadingLang] = useState(null); // lang code currently downloading, or null
    const [confirmOpen, setConfirmOpen] = useState(false);
    const [downloadOpen, setDownloadOpen] = useState(false);
    const [downloadPos, setDownloadPos] = useState({ top: 0, right: 0 });
    const audioRef = useRef(null);
    const downloadRef = useRef(null);

    // Ask for a quality rating only when a job freshly finishes this session —
    // seed the ref with the current status so already-completed history rows
    // don't fire the prompt on mount.
    const requestQualityRating = useGlobalStore((state) => state.requestQualityRating);
    const prevStatusRef = useRef(job.status);
    useEffect(() => {
        const prev = prevStatusRef.current;
        prevStatusRef.current = job.status;
        if (prev !== 'completed' && job.status === 'completed') {
            requestQualityRating({
                tool: 'dubbing',
                source: 'dubbing',
                orgId: org_id,
                integrationId: integration_id,
                meta: { job_id: job.job_id, target_langs_count: (job.target_langs || []).length },
            });
        }
    }, [job.status, job.target_langs, org_id, integration_id, requestQualityRating]);

    // Close the download dropdown on outside click.
    useEffect(() => {
        if (!downloadOpen) return;
        const onClick = e => { if (downloadRef.current && !downloadRef.current.contains(e.target)) setDownloadOpen(false); };
        document.addEventListener('mousedown', onClick);
        return () => document.removeEventListener('mousedown', onClick);
    }, [downloadOpen]);

    const updateDownloadPos = () => {
        if (!downloadRef.current) return;
        const rect = downloadRef.current.getBoundingClientRect();
        setDownloadPos({ top: rect.bottom + 4, right: window.innerWidth - rect.right });
    };


    useEffect(() => {
        if (!downloadOpen) return;
        window.addEventListener('scroll', updateDownloadPos, true);
        window.addEventListener('resize', updateDownloadPos);
        return () => {
            window.removeEventListener('scroll', updateDownloadPos, true);
            window.removeEventListener('resize', updateDownloadPos);
        };
    }, [downloadOpen]);

    const toggleDownloadMenu = () => {
        if (!downloadOpen) updateDownloadPos();
        setDownloadOpen(o => !o);
    };

    // Per-language completed outputs, ready to download (public S3 URLs written by the webhook).
    const languageStatuses = job.language_statuses || {};
    const completed = Object.entries(languageStatuses)
        .filter(([, v]) => v && v.status === 'completed' && (v.final_output_url || v.dubbed_audio_url))
        .map(([lang, v]) => ({ lang, url: v.final_output_url || v.dubbed_audio_url, format: v.format }));

    // Format/playback are real per-language data from the webhook; represent the job by its
    // first completed language (all target languages share the same source audio).
    // No duration shown — each dubbed language can run a different length than the source.
    const representative = completed[0];
    const isTerminal = job.status === 'completed' || job.status === 'partial_failure' || job.status === 'failed';

    // Only one row plays at a time — when another row becomes the active one, stop ours.
    useEffect(() => {
        if (playingId !== job.job_id && isPlaying) audioRef.current?.pause();
    }, [playingId, job.job_id, isPlaying]);

    const togglePlay = async () => {
        if (!representative || loadingAudio) return;
        const audio = audioRef.current;
        if (!audio) return;
        if (isPlaying) {
            audio.pause();
            setIsPlaying(false);
            setPlayingId(null);
            return;
        }
        try {
            setLoadingAudio(true);
            if (audio.src !== representative.url) audio.src = representative.url;
            await audio.play();
            setIsPlaying(true);
            setPlayingId(job.job_id);
        } catch (e) {
            console.error('Playback failed', e);
        } finally {
            setLoadingAudio(false);
        }
    };

    const handleLangDownload = async (lang, url) => {
        if (downloadingLang) return;
        setDownloadingLang(lang);
        try {
            await downloadFile(url, `${job.title || 'dub'}-${lang}.${extOf(url)}`);
            setDownloadOpen(false);
        } catch (e) {
            console.error('Download failed', e);
            toast.error('Download failed. Please try again.');
        } finally {
            setDownloadingLang(null);
        }
    };

    const handleDelete = async () => {
        if (deleteDub.isLoading) return;
        setConfirmOpen(false);
        try {
            await deleteDub.mutateAsync({ org_id, integration_id, job_id: job.job_id });
            toast.success('Dub deleted');
        } catch (err) {
            console.error('Delete dub failed', err);
            toast.error('Failed to delete. Please try again.');
        }
    };

    const playButton = (
        <button
            type="button"
            onClick={togglePlay}
            disabled={!representative || loadingAudio}
            title={representative ? (isPlaying ? 'Pause' : 'Play') : undefined}
            style={{
                width: 36, height: 36, borderRadius: 9, flexShrink: 0, border: 'none', padding: 0,
                background: C.n100, display: 'flex', alignItems: 'center', justifyContent: 'center',
                cursor: representative && !loadingAudio ? 'pointer' : 'default',
            }}
        >
            {loadingAudio
                ? <Loader2 size={15} color={C.n500} className="vi-spin" />
                : representative
                    ? (isPlaying
                        ? <Pause size={15} color={C.n700} fill={C.n700} />
                        : <Play size={15} color={C.n700} fill={C.n700} style={{ marginLeft: 1 }} />)
                    : <Globe size={16} color={C.n500} />}
        </button>
    );

    const languagePills = completed.length > 0 ? (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, justifyContent: isMobile ? 'flex-start' : 'center' }}>
            {completed.map(({ lang }) => (
                <span
                    key={lang}
                    style={{ display: 'inline-flex', alignItems: 'center', padding: '4px 10px', borderRadius: 999, border: `1px solid ${C.n200}`, background: C.w, color: C.n700, fontSize: 12, fontWeight: 600 }}
                >
                    {lang.toUpperCase()}
                </span>
            ))}
        </div>
    ) : (
        (job.target_langs || []).join(', ') || '—'
    );

    const statusNode = job.status === 'failed'
        ? <span style={{ color: '#EF4444' }}>Failed</span>
        : job.status === 'partial_failure'
            ? <span style={{ color: '#B45309' }}>Partial</span>
            : job.status === 'completed'
                ? <span style={{ color: C.g600 }}>Completed</span>
                : isTerminal ? null : 'Processing…';

    const downloadButton = (
        <div ref={downloadRef} style={{ position: 'relative' }}>
            <button
                type="button"
                onClick={toggleDownloadMenu}
                disabled={!completed.length}
                title="Download"
                aria-label="Download"
                style={{
                    width: 28, height: 28, borderRadius: 7, border: 'none',
                    background: downloadOpen ? C.n100 : 'transparent',
                    color: completed.length ? C.n400 : C.n300,
                    cursor: completed.length ? 'pointer' : 'default',
                    display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
                }}
                onMouseEnter={e => { if (completed.length) e.currentTarget.style.background = C.n100; }}
                onMouseLeave={e => { if (!downloadOpen) e.currentTarget.style.background = 'transparent'; }}
            >
                <Download size={15} />
            </button>
            {downloadOpen && (
                <div style={{
                    position: 'fixed', top: downloadPos.top, right: downloadPos.right, zIndex: 1000,
                    background: C.w, border: `1px solid ${C.n200}`, borderRadius: 10,
                    boxShadow: '0 8px 24px rgba(0,0,0,.12)', overflow: 'hidden', minWidth: 140,
                }}>
                    {completed.map(({ lang, url }) => (
                        <button
                            key={lang}
                            type="button"
                            disabled={!!downloadingLang}
                            onClick={() => handleLangDownload(lang, url)}
                            style={{
                                width: '100%', display: 'flex', alignItems: 'center', gap: 8,
                                padding: '9px 12px', border: 'none', background: 'transparent',
                                color: C.n700, fontSize: 13, fontWeight: 500,
                                cursor: downloadingLang ? 'default' : 'pointer',
                                textAlign: 'left', fontFamily: 'inherit',
                            }}
                            onMouseEnter={e => { e.currentTarget.style.background = C.n50; }}
                            onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; }}
                        >
                            {downloadingLang === lang
                                ? <Loader2 size={13} color={C.n400} className="vi-spin" />
                                : <Download size={13} color={C.n400} />}
                            {lang.toUpperCase()}
                        </button>
                    ))}
                </div>
            )}
        </div>
    );

    const deleteButton = (
        <button
            type="button"
            onClick={() => setConfirmOpen(true)}
            disabled={deleteDub.isLoading}
            title="Delete dub"
            aria-label="Delete dub"
            style={{ width: 28, height: 28, borderRadius: 7, border: 'none', background: 'transparent', color: C.n400, cursor: deleteDub.isLoading ? 'default' : 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}
            onMouseEnter={e => { if (!deleteDub.isLoading) e.currentTarget.style.color = '#DC2626'; }}
            onMouseLeave={e => { e.currentTarget.style.color = C.n400; }}
        >
            <Trash2 size={15} />
        </button>
    );

    const confirmDialog = (
        <ConfirmDialog
            open={confirmOpen}
            title="Delete dub"
            message={`Delete "${job.title || 'this dub'}"? This can't be undone.`}
            onConfirm={handleDelete}
            onCancel={() => setConfirmOpen(false)}
        />
    );

    const audioEl = <audio ref={audioRef} onEnded={() => { setIsPlaying(false); setPlayingId(null); }} onPause={() => setIsPlaying(false)} />;

    if (isMobile) {
        return (
            <div style={{
                display: 'flex', flexDirection: 'column', gap: 8,
                padding: '12px 16px', borderTop: `1px solid ${C.n100}`,
                opacity: isTerminal ? 1 : 0.55,
            }}>
                {audioEl}
                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                    {playButton}
                    <div style={{ minWidth: 0, flex: 1 }}>
                        <div style={{ fontSize: 13.5, fontWeight: 600, color: C.n900, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                            {job.title || 'Untitled dub'}
                        </div>
                        <div style={{ fontSize: 11.5, color: C.n400, marginTop: 2 }}>
                            {job.source_type || 'upload'}
                        </div>
                    </div>
                    <div style={{ fontSize: 12.5, color: C.n500, flexShrink: 0 }}>
                        {languagePills}
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 4, flexShrink: 0 }}>
                        {downloadButton}
                        {deleteButton}
                    </div>
                </div>
                {statusNode && (
                    <div style={{ paddingLeft: 46, fontSize: 12.5, color: C.n500 }}>
                        {statusNode}
                    </div>
                )}
                {confirmDialog}
            </div>
        );
    }

    return (
        <div style={{
            display: 'grid', gridTemplateColumns, gap: 12, alignItems: 'center',
            padding: '12px 20px', borderTop: `1px solid ${C.n100}`,
            opacity: isTerminal ? 1 : 0.55,
        }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, minWidth: 0 }}>
                {audioEl}
                {playButton}
                <div style={{ minWidth: 0 }}>
                    <div style={{ fontSize: 13.5, fontWeight: 600, color: C.n900, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {job.title || 'Untitled dub'}
                    </div>
                    <div style={{ fontSize: 11.5, color: C.n400, marginTop: 2 }}>
                        {job.source_type || 'upload'}
                    </div>
                </div>
            </div>
            <div style={{ fontSize: 12.5, color: C.n500, minWidth: 0, textAlign: 'center' }}>
                {languagePills}
            </div>
            <div style={{ fontSize: 12.5, color: C.n500, textAlign: 'center' }}>
                {statusNode}
            </div>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 4 }}>
                {downloadButton}
                {deleteButton}
            </div>
            {confirmDialog}
        </div>
    );
}

function DubGenerations({ org_id, integration_id }) {
    const { data: jobs = [], isLoading } = useDubbingJobs({ org_id, integration_id });
    const [playingId, setPlayingId] = useState(null);

    return (
        <GenerationsTable
            title="Generations"
            count={jobs.length}
            columns={[
                { key: 'lang', label: 'Language', width: '200px' },
                { key: 'status', label: 'Status', width: '90px' },
            ]}
            items={jobs}
            rowKey={job => job.job_id}
            renderRow={(job, gridTemplateColumns, key, isMobile) => (
                <DubRow
                    key={key}
                    job={job}
                    org_id={org_id}
                    integration_id={integration_id}
                    gridTemplateColumns={gridTemplateColumns}
                    playingId={playingId}
                    setPlayingId={setPlayingId}
                    isMobile={isMobile}
                />
            )}
            loading={isLoading}
            emptyIcon={<Globe size={24} color={C.n300} />}
            emptyTitle="No dubs yet"
            emptySubtitle="Upload a file or paste a YouTube link to create your first dub"
        />
    );
}

// ── Main Dubbing page ─────────────────────────────────────────────────────────

export default function Dubbing() {
    const { org_id: orgId, id: integrationId } = useParams();
    const isMobile = useIsMobile();
    const dispatch = useDispatch();

    const [inputTab, setInputTab] = useState('upload');
    const [selectedFile, setSelectedFile] = useState(null);
    const [uploading, setUploading] = useState(false);
    const [uploadError, setUploadError] = useState(null);
    const [youtubeUrl, setYoutubeUrl] = useState('');
    const [youtubeMeta, setYoutubeMeta] = useState(null);
    const [youtubeFetching, setYoutubeFetching] = useState(false);
    const [youtubeError, setYoutubeError] = useState(null);
    const [otherUrl, setOtherUrl] = useState('');
    const [urlMediaType, setUrlMediaType] = useState('video');
    const [sourceLang, setSourceLang] = useState('en');
    const [targetLangs, setTargetLangs] = useState([]);

    const [startTime, setStartTime] = useState('');
    const [endTime, setEndTime] = useState('');
    const createDub = useCreateDubbingJob();

    useEffect(() => {
        if (!YOUTUBE_REGEX.test(youtubeUrl)) {
            setYoutubeMeta(null);
            setYoutubeError(null);
            return;
        }
        let cancelled = false;
        setYoutubeFetching(true);
        setYoutubeError(null);
        API.get('main', `/dubbing/youtube-metadata?url=${encodeURIComponent(youtubeUrl)}`)
            .then(res => { if (!cancelled) setYoutubeMeta(res.data); })
            .catch(() => { if (!cancelled) setYoutubeError('Could not fetch video details. Check the URL and try again.'); })
            .finally(() => { if (!cancelled) setYoutubeFetching(false); });
        return () => { cancelled = true; };
    }, [youtubeUrl]);

    const clearYoutube = () => {
        setYoutubeUrl('');
        setYoutubeMeta(null);
        setYoutubeError(null);
    };

    // Upload the picked file to S3 (presigned) and stash its returned key as s3Key.
    const handleFileSelect = async (file) => {
        if (!file) return;
        if (file.size > MAX_UPLOAD_SIZE_BYTES) {
            setUploadError('File is too large. Max size is 50MB.');
            return;
        }
        setUploadError(null);
        let durationSec;
        try {
            durationSec = await readMediaDuration(file);
        } catch (e) {
            console.error('Could not read duration', e);
            setUploadError('Could not verify file duration. Please try a different file.');
            return;
        }
        if (durationSec > MAX_UPLOAD_DURATION_SEC) {
            setUploadError('File is too long. Maximum 10 minutes allowed.');
            return;
        }
        setSelectedFile({ name: file.name, size: file.size, type: file.type });
        setUploading(true);
        try {
            const { signed_url, url } = await fetchDubbingPresignedUrl({
                org_id: orgId,
                integration_id: integrationId,
                fileName: file.name,
                contentType: contentTypeFor(file),
            });
            await uploadToS3(signed_url, file);
            setSelectedFile({ name: file.name, size: file.size, type: file.type, s3Key: url, durationSec });
        } catch (err) {
            console.error('Dubbing upload failed', err);
            setUploadError('Upload failed. Please try again.');
            setSelectedFile(null);
        } finally {
            setUploading(false);
        }
    };

    const clearUpload = () => { setSelectedFile(null); setUploadError(null); };

    
    const otherUrlIsYoutube = YOUTUBE_REGEX.test(otherUrl);

    // For uploads the source is ready only once the S3 upload has finished (s3Key set).
    const hasSource = inputTab === 'upload'
        ? !!(selectedFile && selectedFile.s3Key)
        : inputTab === 'youtube'
            ? !!youtubeMeta
            : otherUrl.trim().length > 0 && !otherUrlIsYoutube;

    const canDub = hasSource && targetLangs.length > 0 && !uploading;

    const getMediaType = () => {
        if (inputTab === 'youtube') return 'video';
        if (inputTab === 'upload') return isVideoFile(selectedFile) ? 'video' : 'audio';
        return urlMediaType;
    };

    const getSourceUrl = () => {
        if (inputTab === 'youtube') return youtubeUrl;
        if (inputTab === 'upload') return selectedFile?.s3Key || '';
        return otherUrl;
    };

    // Source media duration in seconds, used server-side to estimate/gate the dubbing credit
    // cost (charged per target language). Unknown for arbitrary URLs → null (gate fails open).
    const getSourceDurationSeconds = () => {
        if (inputTab === 'youtube') return youtubeMeta?.durationSeconds ?? null;
        if (inputTab === 'upload') return selectedFile?.durationSec ?? null;
        return null;
    };

    const { insufficientCredits: hasInsufficientCredits } = useFeatureCreditGate({
        org_id: orgId,
        feature: 'dubbing',
        duration_seconds: getSourceDurationSeconds() != null ? Math.round(getSourceDurationSeconds() * targetLangs.length) : null,
        enabled: canDub,
    });

    const handleStartDub = async () => {
        if (!canDub || createDub.isLoading || hasInsufficientCredits) return;
        try {
            const title = youtubeMeta?.title || selectedFile?.name || null;

            dispatch(addMixPanelEvent({
                orgId, integrationId, body: {
                    eventName: "DUBBING_STARTED",
                    eventData: {
                        tool: "dubbing", // GROW-84: tool on every stage for start->finish comparison
                        source_type: inputTab,
                        source_url: getSourceUrl(),
                        media_type: getMediaType(),
                        source_lang: sourceLang,
                        target_langs: targetLangs,
                        target_langs_count: targetLangs.length,
                        title,
                    }
                }
            }));

            await createDub.mutateAsync({
                org_id: orgId,
                integration_id: integrationId,
                body: {
                    sourceType: inputTab,
                    sourceUrl: getSourceUrl(),
                    mediaType: getMediaType(),
                    sourceLang,
                    targetLangs,
                    title,
                },
            });
            // Reset the form so the new job shows in the (invalidated) history list.
            clearUpload();
            clearYoutube();
            setOtherUrl('');
            setTargetLangs([]);
        } catch (err) {
            console.error('Create dub failed', err);
        }
    };

    const TABS = [
        { id: 'upload', label: 'Upload' },
        { id: 'url', label: 'Other URL' },
    ];

    return (
        <div style={{ maxWidth: 720, margin: '0 auto' }}>

            {/* ── Header ── */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 14, marginBottom: 28 }}>
                <div style={{
                    width: 52, height: 52, borderRadius: 14, flexShrink: 0,
                    background: C.n100, border: `1px solid ${C.n200}`,
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                }}>
                    <Globe size={24} color={C.n600} strokeWidth={1.75} />
                </div>
                <h1 style={{ fontSize: 26, fontWeight: 800, letterSpacing: '-.01em', color: C.n900, margin: 0 }}>Create a dub</h1>
            </div>

            {/* ── Widget card ── */}
            <div style={{
                background: C.w, border: `1px solid ${C.n200}`, borderRadius: 16,
                padding: 20, boxShadow: '0 1px 3px rgba(15,23,42,.04)',
            }}>

                {/* ── Source section ── */}
                <div style={{ paddingBottom: 20, borderBottom: `1px solid ${C.n100}` }}>
                    <div style={{ fontSize: 13.5, fontWeight: 600, color: C.n700, marginBottom: 10 }}>
                        Audio or video source
                    </div>

                    {/* Tabs */}
                    <div style={{
                        display: 'inline-flex', width: 'fit-content', background: C.n100, borderRadius: 10,
                        padding: 4, gap: 2, marginBottom: 12,
                    }}>
                        {TABS.map(tab => (
                            <button
                                key={tab.id}
                                type="button"
                                onClick={() => setInputTab(tab.id)}
                                style={{
                                    padding: '6px 16px', borderRadius: 8, border: 'none',
                                    background: inputTab === tab.id ? C.w : 'transparent',
                                    color: inputTab === tab.id ? C.n800 : C.n500,
                                    fontSize: 13.5, fontWeight: inputTab === tab.id ? 600 : 400,
                                    cursor: 'pointer', fontFamily: 'inherit',
                                    boxShadow: inputTab === tab.id ? '0 1px 3px rgba(0,0,0,.1)' : 'none',
                                    transition: 'all .12s',
                                }}
                            >
                                {tab.label}
                            </button>
                        ))}
                    </div>

                    {/* Input area */}
                    {inputTab === 'upload' && (
                        <UploadZone
                            onFileSelect={handleFileSelect}
                            selectedFile={selectedFile}
                            uploading={uploading}
                            error={uploadError}
                            onClear={clearUpload}
                        />
                    )}
                    {inputTab === 'youtube' && (
                        youtubeMeta ? (
                            <YouTubePreview metadata={youtubeMeta} onClear={clearYoutube} />
                        ) : (
                            <div>
                                <URLInput
                                    value={youtubeUrl}
                                    onChange={val => { setYoutubeUrl(val); setYoutubeError(null); }}
                                    placeholder="https://youtube.com/watch?v=…"
                                />
                                {youtubeFetching && (
                                    <p style={{ fontSize: 12.5, color: C.n400, margin: '8px 0 0' }}>
                                        Fetching video details…
                                    </p>
                                )}
                                {youtubeError && !youtubeFetching && (
                                    <p style={{ fontSize: 12.5, color: C.r500, margin: '8px 0 0' }}>
                                        {youtubeError}
                                    </p>
                                )}
                                {youtubeUrl && !YOUTUBE_REGEX.test(youtubeUrl) && !youtubeFetching && (
                                    <p style={{ fontSize: 12.5, color: C.a500, margin: '8px 0 0' }}>
                                        Paste a valid YouTube video link to continue.
                                    </p>
                                )}
                            </div>
                        )
                    )}
                    {inputTab === 'url' && (
                        <>
                            <URLInput
                                value={otherUrl}
                                onChange={setOtherUrl}
                                placeholder="https://…"
                            />
                            <MediaTypeToggle value={urlMediaType} onChange={setUrlMediaType} />
                            {otherUrlIsYoutube && (
                                <p style={{ fontSize: 12.5, color: C.r500, margin: '8px 0 0' }}>
                                    We don't support YouTube videos as of now. You can download the video and upload it instead.
                                </p>
                            )}
                        </>
                    )}

                    {/* Contextual note */}
                    <p style={{ fontSize: 12.5, color: C.n400, margin: '10px 0 0' }}>
                        {inputTab === 'upload' ? (
                            <>
                                Your video will be dubbed in standard resolution and will include a watermark.{' '}
                                <Link to={`/organisation/${orgId}/plans`} style={{ color: C.p600, textDecoration: 'none', fontWeight: 500 }}>
                                    Upgrade your plan
                                </Link>{' '}
                                to remove it.
                            </>
                        ) : inputTab === 'youtube' ? (
                            'Paste a YouTube link to dub the video directly.'
                        ) : (
                            'Paste a direct link to a publicly accessible audio or video file.'
                        )}
                    </p>
                </div>

                {/* ── Source Language ── */}
                <div style={{ padding: '20px 0', borderBottom: `1px solid ${C.n100}` }}>
                    <div style={{ fontSize: 13.5, fontWeight: 600, color: C.n700, marginBottom: 10 }}>
                        Source Language
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                        <SearchableSelect
                            selected={sourceLang}
                            onChange={setSourceLang}
                            options={DUBBING_LANGUAGES.map(l => ({ value: l.code, label: l.name }))}
                        />
                        <ArrowRight size={16} color={C.n300} style={{ flexShrink: 0 }} />
                        <SearchableSelect
                            selected={targetLangs}
                            onChange={setTargetLangs}
                            options={DUBBING_LANGUAGES.map(l => ({ value: l.code, label: l.name }))}
                            multi
                            placeholder="Select languages"
                        />
                    </div>
                </div>

                {/* ── Time range ── */}
                <div style={{
                    display: 'flex', flexDirection: isMobile ? 'column' : 'row',
                    alignItems: isMobile ? 'stretch' : 'center', justifyContent: 'space-between',
                    padding: '20px 0', gap: isMobile ? 10 : 16, borderBottom: `1px solid ${C.n100}`,
                }}>
                    <div style={{ fontSize: 13.5, fontWeight: 600, color: C.n700, flexShrink: 0 }}>
                        Time range to dub
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <input
                            type="text"
                            placeholder="hh:mm:ss"
                            value={startTime}
                            onChange={e => setStartTime(e.target.value)}
                            style={{
                                width: isMobile ? '100%' : 100, minWidth: 0, padding: '8px 12px', border: `1px solid ${C.n200}`,
                                borderRadius: 8, fontSize: 13.5, color: C.n800, fontFamily: 'inherit',
                                outline: 'none', textAlign: 'center', boxSizing: 'border-box',
                            }}
                            onFocus={e => { e.target.style.borderColor = C.p600; e.target.style.boxShadow = `0 0 0 3px ${C.p100}`; }}
                            onBlur={e => { e.target.style.borderColor = C.n200; e.target.style.boxShadow = 'none'; }}
                        />
                        <ArrowRight size={14} color={C.n300} style={{ flexShrink: 0 }} />
                        <input
                            type="text"
                            placeholder="hh:mm:ss"
                            value={endTime}
                            onChange={e => setEndTime(e.target.value)}
                            style={{
                                width: isMobile ? '100%' : 100, minWidth: 0, padding: '8px 12px', border: `1px solid ${C.n200}`,
                                borderRadius: 8, fontSize: 13.5, color: C.n800, fontFamily: 'inherit',
                                outline: 'none', textAlign: 'center', boxSizing: 'border-box',
                            }}
                            onFocus={e => { e.target.style.borderColor = C.p600; e.target.style.boxShadow = `0 0 0 3px ${C.p100}`; }}
                            onBlur={e => { e.target.style.borderColor = C.n200; e.target.style.boxShadow = 'none'; }}
                        />
                    </div>
                </div>

                {/* ── Submit ── */}
                <div style={{ paddingTop: 20 }}>
                    {!canDub && (
                        <p style={{ fontSize: 13, color: C.n400, textAlign: 'center', margin: '0 0 12px' }}>
                            {!hasSource ? 'Please provide a valid source.' : 'Please select at least one target language.'}
                        </p>
                    )}
                    {canDub && (
                        <div style={{ textAlign: 'center', margin: '0 0 12px' }}>
                            <FeatureCreditCost
                                orgId={orgId}
                                feature="dubbing"
                                durationSeconds={getSourceDurationSeconds() != null ? Math.round(getSourceDurationSeconds() * targetLangs.length) : null}
                                enabled={canDub}
                            />
                        </div>
                    )}
                    <button
                        type="button"
                        onClick={handleStartDub}
                        disabled={!canDub || createDub.isLoading || hasInsufficientCredits}
                        title={hasInsufficientCredits ? 'Not enough credits to create this dub' : ''}
                        style={{
                            width: '100%', padding: '14px', borderRadius: 12, border: 'none',
                            background: canDub && !hasInsufficientCredits ? C.n900 : '#D1D5DB',
                            color: canDub && !hasInsufficientCredits ? C.w : '#9CA3AF',
                            fontSize: 16, fontWeight: 600, cursor: canDub && !hasInsufficientCredits ? 'pointer' : 'not-allowed',
                            fontFamily: 'inherit', transition: 'all .15s',
                        }}
                    >
                        {createDub.isLoading ? 'Creating dub…' : 'Create dub'}
                    </button>
                </div>

            </div>

            {/* ── Generations ── */}
            <DubGenerations org_id={orgId} integration_id={integrationId} />

        </div>
    );
}
